import React, { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Activity,
  LayoutDashboard,
  CalendarDays,
  UserPlus,
  Users,
  UserCog,
  UserCheck,
  Shield,
  Building2,
  ClipboardList,
  ListChecks,
  BarChart3,
  Phone,
  LogOut,
  Settings,
  Calculator,
  FileCheck2,
  LifeBuoy,
  FormInput,
  CircleUser,
  TrendingUp,
  MapPin,
  Briefcase,
  Gauge,
  Headset,
  DollarSign,
  FileSignature,
  Handshake,
  Receipt,
  SlidersHorizontal,
  BellRing,
  MessageSquareText,
  Workflow,
  Zap,
} from 'lucide-react';

import { cn } from '../../lib/utils';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '../ui/navigation-menu';

/**
 * Navigation configuration with permission requirements
 * Each item can have:
 * - requiredPermissions: array of permissions (user needs ANY of them)
 * - requiredAllPermissions: array of permissions (user needs ALL of them)
 * Items without permission requirements are shown to all authenticated users
 */
// The two front-line roles see only their own work (Nic 2026-10-09): a
// Sales Rep's or Onboarding Specialist's menu is their own page, Coverage
// Map and Support (no Dashboard since Nic 2026-10-12). hideForOnlyRoles hides an item when EVERY role
// the person holds is in this list (anyone with another role still sees it).
const FRONT_LINE_ROLES = ['onboarding_support_specialist', 'onboarding_specialist'];

const NAV_CONFIG = [
  {
    type: 'link',
    label: 'Dashboard',
    url: '/dashboard',
    icon: LayoutDashboard,
    hideForOnlyRoles: FRONT_LINE_ROLES,
    requiredPermissions: [
      'claims:view_own',
      'claims:view_assigned',
      'claims:view_all',
    ],
  },
  {
    type: 'link',
    label: 'Calendar',
    url: '/calendar',
    icon: CalendarDays,
    hideForOnlyRoles: FRONT_LINE_ROLES,
    requiredPermissions: ['calendar:view_own', 'calendar:view_all'],
  },
  {
    type: 'link',
    label: 'My Performance',
    url: '/my-performance',
    icon: TrendingUp,
    requiredPermissions: ['kpi:view_own', 'kpi:view_all'],
  },
  {
    type: 'dropdown',
    label: 'Operations',
    icon: Briefcase,
    // No parent requiredPermissions: visibility derives purely from visible
    // children (filterNavItems drops a dropdown with zero visible items). This is
    // future-proof — add a child with a new permission and the menu shows for
    // anyone who can see that child, with no parent "union" list to maintain.
    // Ordered by the funnel: Sales (the Sales Rep, lead to signed), then
    // Client Onboarding (the Onboarding Specialist, signed to a Claims
    // Manager). Lead Handoffs retired 2026-09-28.
    items: [
      {
        label: 'Sales',
        url: '/operations/onboarding-support',
        icon: Headset,
        description: 'New leads, the 10-day cadence, and signing them up',
        // Viewers find it too (boards:view, Nic 2026-10-23).
        requiredPermissions: ['boards:view', 'onboarding_board:work', 'onboarding_board:manage'],
      },
      {
        // 2026-09-28: signed clients, worked through the checklist to
        // Ready for CM (replaced the Case Readiness slide-out).
        label: 'Onboarding Workspace',
        url: '/operations/client-onboarding',
        icon: ListChecks,
        description: 'Signed clients, from their first call to a Claims Manager',
        requiredPermissions: ['boards:view', 'client_onboarding:work', 'client_onboarding:manage'],
      },
      {
        // 2026-10-09: the handoff from Client Onboarding to a Claims
        // Manager, on its own page.
        label: 'CM Assignment',
        url: '/operations/cm-assignment',
        icon: UserCheck,
        description: 'Hand ready clients to their Claims Manager',
        requiredPermissions: ['cm_assignment:manage'],
      },
      {
        label: 'Events',
        url: '/operations/events',
        icon: CalendarDays,
        description: 'Sign-up events and their QR codes',
        requiredPermissions: ['leads:view', 'leads:manage'],
      },
      {
        label: 'Lead Metrics',
        url: '/operations/lead-metrics',
        icon: Gauge,
        description: 'The funnel, the cadence SLA scorecard, and lead history',
        requiredPermissions: [
          'leads:view',
          'leads:manage',
          'onboarding_board:metrics',
        ],
      },
      {
        label: 'Partners',
        url: '/operations/partners',
        icon: Building2,
        description: 'Referral partners + application review',
        requiredPermissions: ['partners:view'],
      },
      {
        label: 'Referral Payouts',
        url: '/operations/referral-payouts',
        icon: DollarSign,
        description: '$250 referral rewards owed + paid',
        requiredPermissions: [
          'referral_payouts:view',
          'referral_payouts:manage',
        ],
      },
    ],
  },
  {
    type: 'dropdown',
    label: 'Billing',
    icon: Receipt,
    // Visibility derives from visible children, same as Operations.
    items: [
      {
        label: 'Invoices',
        url: '/billing/invoices',
        icon: Receipt,
        description: 'All client invoices, reminders, and engagement',
        requiredPermissions: ['billing:view', 'billing:manage'],
      },
      {
        label: 'Settings',
        url: '/billing/settings',
        icon: SlidersHorizontal,
        description: 'Reminder cadence, exclusions, payment plans',
        requiredPermissions: ['billing:manage'],
      },
      {
        label: 'VA Run Board',
        url: '/billing/va-runs',
        icon: Activity,
        description: 'Live status of the monthly automated rating checks',
        requiredPermissions: ['billing:view', 'billing:manage'],
      },
      {
        label: 'Envoy',
        url: '/billing/envoy',
        icon: Handshake,
        description: 'Envoy commissions owed, paid, and recoup, client by client',
        requiredPermissions: ['envoy_finance:view', 'envoy_finance:manage'],
      },
      {
        label: 'VA Calls',
        url: '/admin/va_payments',
        icon: Phone,
        description: 'VA payment call tracking and review',
        requiredPermissions: ['reports:financial'],
      },
    ],
  },
  {
    type: 'dropdown',
    label: 'User Management',
    icon: Users,
    hideForOnlyRoles: FRONT_LINE_ROLES,
    requiredPermissions: [
      'users:view',
      'clients:manage',
      'providers:manage',
      'admins:manage',
      'invitations:view_all',
      'invitations:view_own',
      'users:manage_roles',
      'trainees:manage',
    ],
    items: [
      {
        label: 'Invitations',
        url: '/admin/invitations',
        icon: UserPlus,
        description: 'Send registration invites to new clients',
        requiredPermissions: ['invitations:view_all', 'invitations:view_own'],
      },
      {
        label: 'Clients',
        url: '/admin/users',
        icon: Users,
        description: 'Manage existing client accounts',
        requiredPermissions: ['users:view'],
      },
      {
        label: 'Providers',
        url: '/admin/providers',
        icon: UserCog,
        description: 'Healthcare providers for claims',
        requiredPermissions: ['users:view'],
      },
      {
        label: 'Internal Users',
        url: '/admin/admins',
        icon: Shield,
        description: 'Manage internal team members',
        // Manage Trainees (2026-10-30): Claims Managers only, one action.
        requiredPermissions: ['admins:manage', 'trainees:manage'],
      },
      {
        label: 'Role Management',
        url: '/admin/roles',
        icon: Shield,
        description: 'Manage user roles and permissions',
        requiredPermissions: ['users:manage_roles'],
      },
    ],
  },
  {
    type: 'dropdown',
    label: 'Analytics',
    icon: BarChart3,
    requiredPermissions: [
      'reports:claims_metrics',
      'reports:onboarding',
      'kpi:view_all',
      'coverage_map:view',
    ],
    items: [
      {
        label: 'Coverage Map',
        url: '/coverage-map',
        icon: MapPin,
        description: 'See where clients and providers are located',
        requiredPermissions: ['coverage_map:view'],
      },
      {
        label: 'Team Performance',
        url: '/my-performance',
        icon: TrendingUp,
        description: 'View claims manager KPI dashboards',
        requiredPermissions: ['kpi:view_all'],
      },
      {
        label: 'Claims Manager Metrics',
        url: '/admin/metrics/claims-managers',
        icon: BarChart3,
        description: 'Performance metrics for claims managers',
        requiredPermissions: ['reports:claims_metrics'],
      },
      {
        label: 'Onboarding Metrics',
        url: '/admin/metrics/onboarding',
        icon: ClipboardList,
        description: 'Onboarding specialist performance',
        requiredPermissions: ['reports:onboarding'],
      },
    ],
  },
  {
    type: 'dropdown',
    label: 'Admin',
    icon: Settings,
    requiredPermissions: [
      'admin:va_rates',
      'admin:dbq_forms',
      'admin:support_forms',
      'admin:system_settings',
      'documents:manage',
      'messages:manage',
      'onboarding_cadence:edit',
    ],
    items: [
      // Admins only (Nic 2026-10-23: "keep it simple for Jorge"): the words,
      // the cadence and what runs on its own.
      {
        label: 'Messages',
        url: '/operations/messages',
        icon: MessageSquareText,
        description: 'Every Sales and Onboarding message, worded for where each person came from',
        requiredPermissions: ['messages:manage'],
      },
      {
        label: 'Cadence Editor',
        url: '/admin/cadence-editor',
        icon: Workflow,
        description: "The 10-day cadence every new lead's run follows",
        requiredPermissions: ['onboarding_cadence:edit'],
      },
      {
        label: 'Automations',
        url: '/admin/automations',
        icon: Zap,
        description: 'Auto nurture, Everyone busy and Auto stall: their texts and timing',
        requiredPermissions: ['messages:manage'],
      },
      {
        label: 'VA Rates',
        url: '/admin/va-rates',
        icon: Calculator,
        description: 'Manage VA disability payment rate schedules',
        requiredPermissions: ['admin:va_rates'],
      },
      {
        label: 'Notifications',
        url: '/admin/notification-settings',
        icon: BellRing,
        description: 'Who gets pinged on new leads and hand-offs',
        requiredPermissions: ['admin:system_settings'],
      },
      {
        label: 'Documents',
        url: '/admin/documents',
        icon: FileSignature,
        description: 'Manage documents used for e-signing',
        requiredPermissions: ['documents:manage'],
      },
      {
        label: 'DBQ Forms',
        url: '/dbq',
        icon: FileCheck2,
        description: 'Disability Benefits Questionnaire templates',
        requiredPermissions: ['admin:dbq_forms'],
      },
      {
        label: 'Support Forms',
        url: '/admin/support-forms',
        icon: FormInput,
        description: 'Manage dynamic support forms and Monday.com mappings',
        requiredPermissions: ['admin:support_forms'],
      },
    ],
  },
  {
    type: 'link',
    label: 'My Profile',
    url: '/provider/settings',
    icon: CircleUser,
    requiredPermissions: ['provider:self_service'],
  },
  {
    type: 'link',
    label: 'Support',
    url: '/support',
    icon: LifeBuoy,
    requiredPermissions: ['support:submit'],
  },
];

/**
 * Check if user has any of the required permissions
 */
const hasAnyPermission = (userPermissions, requiredPermissions) => {
  if (!requiredPermissions || requiredPermissions.length === 0) return true;
  if (!userPermissions || userPermissions.length === 0) return false;
  return requiredPermissions.some((perm) => userPermissions.includes(perm));
};

/**
 * Check if user has any of the required roles
 */
const hasAnyRole = (userRoles, requiredRoles) => {
  if (!requiredRoles || requiredRoles.length === 0) return true;
  if (!userRoles || userRoles.length === 0) return false;
  return requiredRoles.some((role) => userRoles.includes(role));
};

/**
 * Check if a nav item is visible to the user
 */
const hiddenForRoles = (item, userRoles) =>
  !!item.hideForOnlyRoles &&
  !!userRoles?.length &&
  userRoles.every((r) => item.hideForOnlyRoles.includes(r));

const isItemVisible = (item, userPermissions, userRoles) => {
  return (
    hasAnyPermission(userPermissions, item.requiredPermissions) &&
    hasAnyRole(userRoles, item.requiredRoles) &&
    !hiddenForRoles(item, userRoles)
  );
};

/**
 * Filter navigation items based on user permissions and roles
 */
const filterNavItems = (items, userPermissions, userRoles) => {
  return items
    .filter((item) => isItemVisible(item, userPermissions, userRoles))
    .map((item) => {
      if (item.type === 'dropdown' && item.items) {
        const filteredSubItems = item.items.filter((subItem) =>
          isItemVisible(subItem, userPermissions, userRoles),
        );
        // Only include dropdown if it has visible items
        if (filteredSubItems.length === 0) return null;
        return { ...item, items: filteredSubItems };
      }
      return item;
    })
    .filter(Boolean);
};

const ListItem = React.forwardRef(
  ({ className, title, children, icon: Icon, href, ...props }, ref) => {
    const navigate = useNavigate();
    const location = useLocation();
    const isActive = location.pathname === href;

    return (
      <li>
        <NavigationMenuLink asChild>
          <button
            ref={ref}
            onClick={() => navigate(href)}
            className={cn(
              'block w-full select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors text-left',
              'hover:bg-white/10 hover:text-white focus:bg-white/10 focus:text-white',
              isActive && 'bg-white/15 text-white',
              className,
            )}
            {...props}
          >
            <div className="flex items-center gap-2 text-sm font-medium leading-none text-white">
              {Icon && <Icon className="h-4 w-4" />}
              {title}
            </div>
            {children && (
              <p className="line-clamp-2 text-sm leading-snug text-slate-400">
                {children}
              </p>
            )}
          </button>
        </NavigationMenuLink>
      </li>
    );
  },
);
ListItem.displayName = 'ListItem';

const HeaderNavigation = ({ user, onSignOut }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Filter navigation based on user permissions
  const links = useMemo(() => {
    const userPermissions = user?._permissions || [];
    const userRoles = user?._roles || [];
    return filterNavItems(NAV_CONFIG, userPermissions, userRoles);
  }, [user?._permissions, user?._roles]);

  const isActive = (url) => location.pathname === url;
  const isDropdownActive = (items) =>
    items?.some((item) => location.pathname === item.url);

  return (
    <NavigationMenu>
      <NavigationMenuList className="flex items-center gap-0.5 2xl:gap-1">
        {links.map((link, idx) => {
          if (link.type === 'dropdown') {
            const Icon = link.icon;
            return (
              <NavigationMenuItem key={idx}>
                <NavigationMenuTrigger
                  className={cn(
                    'bg-transparent text-white/90 hover:text-white',
                    isDropdownActive(link.items) && 'bg-white/10 text-white',
                  )}
                >
                  <Icon className="mr-2 h-4 w-4" />
                  {link.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[280px] gap-1 p-2 mb-0">
                    {link.items.map((item) => (
                      <ListItem
                        key={item.url}
                        title={item.label}
                        href={item.url}
                        icon={item.icon}
                      >
                        {item.description}
                      </ListItem>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            );
          }

          const Icon = link.icon;
          return (
            <NavigationMenuItem key={idx}>
              <NavigationMenuLink asChild>
                <button
                  onClick={() => navigate(link.url)}
                  className={cn(
                    navigationMenuTriggerStyle(),
                    'bg-transparent text-white/90 hover:text-white',
                    isActive(link.url) && 'bg-white/10 text-white',
                  )}
                >
                  <Icon className="mr-2 h-4 w-4" />
                  {link.label}
                </button>
              </NavigationMenuLink>
            </NavigationMenuItem>
          );
        })}

        {/* Sign Out Button */}
        <NavigationMenuItem>
          <NavigationMenuLink asChild>
            <button
              onClick={() => onSignOut()}
              className={cn(
                navigationMenuTriggerStyle(),
                'bg-transparent text-white/90 hover:text-white hover:bg-red-500/20',
              )}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </button>
          </NavigationMenuLink>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
};

// Mobile navigation: a hamburger INSIDE the black header that opens a dark
// slide-down menu (2026-07-30 — replaced the old floating <select> pill that
// sat orphaned under the header and read as makeshift). Flattens dropdown
// groups into labeled sections.
const MobileNavigation = ({ user, onSignOut, forceShow = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);

  // Filter navigation based on user permissions
  const links = useMemo(() => {
    const userPermissions = user?._permissions || [];
    const userRoles = user?._roles || [];
    return filterNavItems(NAV_CONFIG, userPermissions, userRoles);
  }, [user?._permissions, user?._roles]);

  // Close on navigation.
  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const go = (url) => {
    setOpen(false);
    navigate(url);
  };

  const itemButton = (item, indent = false) => (
    <button
      key={item.url}
      type="button"
      onClick={() => go(item.url)}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[0.95rem] font-medium text-white/85 transition-colors',
        'hover:bg-white/10 hover:text-white',
        indent && 'pl-6',
        location.pathname === item.url && 'bg-white/15 text-white',
      )}
    >
      {item.icon ? <item.icon className="h-4 w-4 flex-shrink-0" /> : null}
      {item.label}
    </button>
  );

  // Hamburger takes over below xl: with ~9 permission-gated menus the
  // desktop row cannot fit mid-size windows even compressed. forceShow: the
  // layout measured that the full row doesn't fit even above xl.
  return (
    <div className={forceShow ? '' : 'xl:hidden'}>
      <button
        type="button"
        aria-label="Menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-10 w-10 items-center justify-center rounded-lg text-white/90 transition-colors hover:bg-white/10 hover:text-white"
      >
        {open ? (
          // Inline X so we avoid pulling another icon set into the header.
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        ) : (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        )}
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-full z-50 border-t border-white/10 bg-black/95 px-3 pb-4 pt-2 shadow-xl backdrop-blur">
          {links.map((link, idx) => {
            if (link.type === 'dropdown') {
              return (
                <div key={idx} className="mt-1">
                  <div className="px-3 pb-1 pt-2 text-[0.68rem] font-bold uppercase tracking-wider text-white/40">
                    {link.label}
                  </div>
                  {link.items.map((item) => itemButton(item, true))}
                </div>
              );
            }
            return itemButton(link);
          })}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
            className="mt-2 flex w-full items-center gap-3 rounded-lg border-t border-white/10 px-3 py-2.5 pt-3 text-left text-[0.95rem] font-medium text-white/85 transition-colors hover:bg-red-500/20 hover:text-white"
          >
            <LogOut className="h-4 w-4 flex-shrink-0" />
            Sign Out
          </button>
        </div>
      ) : null}
    </div>
  );
};

export { HeaderNavigation, MobileNavigation };
